import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ujmsRunDemoJourney } from "../../services/ujmsApi";
import { Panel, cls } from "../ui";
import { PlayCircle, Loader2, CheckCircle2, XCircle } from "lucide-react";

const DEPT_COLORS = {
  "Law Enforcement": "border-slate-400 bg-slate-100 text-slate-700",
  "Sheriff / Corrections": "border-amber-300 bg-amber-50 text-amber-800",
  "State's Attorney": "border-red-300 bg-red-50 text-red-800",
  "Clerk of Court": "border-blue-300 bg-blue-50 text-blue-800",
  "Pretrial Services": "border-teal-300 bg-teal-50 text-teal-800",
  "Judiciary": "border-purple-300 bg-purple-50 text-purple-800",
  "Courtroom": "border-purple-300 bg-purple-50 text-purple-800",
  "Defense Bar": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "Discovery": "border-indigo-300 bg-indigo-50 text-indigo-800",
  "Motions": "border-indigo-300 bg-indigo-50 text-indigo-800",
  "Finance": "border-green-300 bg-green-50 text-green-800",
  "Probation": "border-orange-300 bg-orange-50 text-orange-800",
};

export function DemoJourneyTab() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await ujmsRunDemoJourney();
      setResult(r.data);
      if (r.data.success) toast.success(`Journey complete — ${r.data.case_number} in ${r.data.duration_seconds}s`);
      else toast.error("Journey stopped on a failing step — see the timeline");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Journey failed to start");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="demo-journey-tab">
      <Panel title="Stakeholder demo — replay the complete arrest-to-sentencing journey" testId="demo-journey-panel">
        <div className="p-4 space-y-3">
          <p className="text-sm text-slate-600 max-w-3xl">
            Runs the full 18-step justice workflow live against this system with a brand-new fictional defendant:
            arrest → booking → referral → charging → intake → first appearance → pretrial assessment → release order
            → hearing → defense appearance → discovery → motion ruling → custody release → plea → sentencing →
            payment → probation → final disposition. Every step is executed by the correct department role and lands
            on the certified docket, the audit log and the Integration Hub.
          </p>
          <button className="inline-flex items-center gap-2 bg-blue-900 text-white text-sm font-semibold px-4 py-2 rounded-sm hover:bg-blue-800 disabled:opacity-50 transition-colors"
            disabled={busy} onClick={run} data-testid="demo-journey-run-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            {busy ? "Running the journey — usually under 30 seconds…" : "Run Demo Journey"}
          </button>
        </div>
      </Panel>

      {result && (
        <Panel title={result.success
          ? `Journey complete — ${result.case_number} · ${result.defendant} · ${result.duration_seconds}s`
          : "Journey stopped — a step failed"} testId="demo-journey-result">
          <div className="p-4 space-y-1.5">
            {result.case_id && (
              <p className="text-sm mb-3">
                <Link to={`/ujms/cases/${result.case_id}`} className="font-mono text-blue-900 font-semibold hover:underline" data-testid="demo-journey-case-link">
                  Open case {result.case_number} →
                </Link>
              </p>
            )}
            <ol className="space-y-1.5">
              {result.steps.map((st) => (
                <li key={st.n} className="flex items-start gap-2.5" data-testid={`demo-step-${st.n}`}>
                  {st.status === "OK"
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                    : <XCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />}
                  <span className={cls("text-[10px] font-semibold uppercase tracking-wide border rounded-sm px-1.5 py-0.5 mt-0.5 whitespace-nowrap",
                    DEPT_COLORS[st.department] || "border-slate-300 bg-slate-50 text-slate-600")}>
                    {st.department}
                  </span>
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-slate-900">{st.n}. {st.title}</span>
                    <span className="text-xs text-slate-400 ml-2">{st.actor} · {st.ms}ms</span>
                    <div className={cls("text-xs", st.status === "OK" ? "text-slate-500" : "text-red-700 font-medium")}>{st.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </Panel>
      )}
    </div>
  );
}
